// ID_CREATED*************************

import React, { useState, useMemo, useCallback, memo } from "react";
import moment from "moment-timezone";
import { useCookies } from 'react-cookie';

//REDUX
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as reduxActions from "store/actions";
import reduxStore from "store/";
import history from "store/history";

//COMPONENTS
import Button from "@mui/material/Button";
import CustomButton from "components/Buttons/custom";
import Card from "components/Card";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Icon from "components/Icon";
import Checklist from "components/Checklist";
import ErrorBoundary from "components/ErrorBoundary";
import SelectCardTypes from "componentsV3/SelectCardTypes";
import ButtonOptions from "componentsV3/Buttons";


//FUNCTIONS
import { translate, customApp, nl2br } from "functions/";
import { setMyDay } from "functions/cards";

//ICONS
import MockupApp from "assets/img/dashboard/Mockup_App_WEB.png";
import MockupSupport from "assets/img/dashboard/supportMockup.png";

// Componente Task Card Moderno
const ModernTaskCard = memo(({ task, onTaskClick, onAddToMyDay, isFromMyDay = false, index }) => {
    const [isHovered, setIsHovered] = useState(false);

    const getTaskPriority = useCallback((task) => {
        const today = moment().format("YYYY-MM-DD");
        const endDate = task.endDate ? moment(task.endDate * 1000).format("YYYY-MM-DD") : null;
        const startDate = task.startDate ? moment(task.startDate * 1000).format("YYYY-MM-DD") : null;

        if (endDate && moment(endDate).isBefore(today)) {
            return { level: 'high', color: '#f44336', label: 'Atrasada', icon: 'warning' };
        }
        if (endDate && moment(endDate).isSame(today)) {
            return { level: 'urgent', color: '#ff9800', label: 'Hoje', icon: 'today' };
        }
        if (endDate && moment(endDate).diff(today, 'days') <= 3) {
            return { level: 'medium', color: '#2196f3', label: 'Próxima', icon: 'schedule' };
        }
        return { level: 'low', color: '#4caf50', label: 'Normal', icon: 'check_circle_outline' };
    }, []);

    const priority = getTaskPriority(task);

    const cardStyles = useMemo(() => ({
        container: {
            background: `linear-gradient(135deg, 
                ${priority.color}10 0%, 
                rgba(255,255,255,0.95) 20%, 
                rgba(255,255,255,0.98) 100%)`,
            backdropFilter: "blur(10px)",
            border: `1px solid ${priority.color}30`,
            borderLeft: `4px solid ${priority.color}`,
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "12px",
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "relative",
            overflow: "hidden",
            animationDelay: `${index * 0.05}s`,
            animation: "slideInLeft 0.5s ease-out forwards",
            opacity: 0,
            transform: isHovered ? "translateX(8px)" : "translateX(0)",
            boxShadow: isHovered ?
                `0 12px 24px ${priority.color}20, 0 0 0 1px ${priority.color}20` :
                "0 2px 8px rgba(0,0,0,0.08)"
        },
        header: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "8px"
        },
        titleSection: {
            flex: 1,
            marginRight: "12px"
        },
        title: {
            fontSize: "14px",
            fontWeight: "700",
            color: "#1a1a1a",
            marginBottom: "4px",
            lineHeight: "1.3",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            textOverflow: "ellipsis"
        },
        taskType: {
            fontSize: "10px",
            color: "#666",
            fontWeight: "500",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            backgroundColor: "#f5f5f5",
            padding: "2px 6px",
            borderRadius: "8px",
            display: "inline-block"
        },
        priorityBadge: {
            display: "flex",
            alignItems: "center",
            gap: "4px",
            backgroundColor: priority.color,
            color: "#fff",
            padding: "4px 8px",
            borderRadius: "12px",
            fontSize: "10px",
            fontWeight: "600",
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            boxShadow: `0 2px 8px ${priority.color}40`
        },
        dateInfo: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "11px",
            color: "#666",
            marginTop: "8px"
        },
        actionButton: {
            position: "absolute",
            top: "8px",
            right: "8px",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            backgroundColor: priority.color,
            color: "#fff",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            transition: "all 0.2s ease",
            opacity: isHovered ? 1 : 0.7,
            transform: isHovered ? "scale(1.1)" : "scale(1)"
        }
    }), [priority, isHovered, index]);

    const formatDate = useCallback((timestamp) => {
        if (!timestamp) return '';
        return moment(timestamp * 1000).format('DD/MM');
    }, []);

    return (
        <>
            <style jsx>{`
                @keyframes slideInLeft {
                    from {
                        opacity: 0;
                        transform: translateX(-30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
            `}</style>
            <div
                style={cardStyles.container}
                onClick={() => onTaskClick && onTaskClick(task)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div style={cardStyles.header}>
                    <div style={cardStyles.titleSection}>
                        <div style={cardStyles.title}>
                            {task.name}
                        </div>
                        <div style={cardStyles.taskType}>
                            {translate(`$__${task.type}`, "*") || task.type}
                        </div>
                    </div>
                    <div style={cardStyles.priorityBadge}>
                        <span className="material-icons" style={{ fontSize: "10px" }}>
                            {priority.icon}
                        </span>
                        {priority.label}
                    </div>
                </div>

                <div style={cardStyles.dateInfo}>
                    {task.startDate && (
                        <span>📅 Início: {formatDate(task.startDate)}</span>
                    )}
                    {task.endDate && (
                        <span>🎯 Fim: {formatDate(task.endDate)}</span>
                    )}
                </div>

                {!isFromMyDay && onAddToMyDay && (
                    <button
                        style={cardStyles.actionButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            onAddToMyDay(task);
                        }}
                        title={translate("$__addOnMyDay")}
                    >
                        <span className="material-icons" style={{ fontSize: "14px" }}>
                            add
                        </span>
                    </button>
                )}
            </div>
        </>
    );
});

// Header Moderno para Seções
const SectionHeader = memo(({ title, subtitle, icon, onFilterChange, currentFilter, showSuggestions, onToggleSuggestions }) => {
    return (
        <div style={{
            background: `linear-gradient(135deg, 
                rgba(255,255,255,0.95) 0%, 
                rgba(255,255,255,0.8) 100%)`,
            backdropFilter: "blur(20px)",
            borderRadius: "16px",
            padding: "20px",
            marginBottom: "20px",
            border: `1px solid ${customApp('menu')}20`,
            boxShadow: `0 4px 16px ${customApp('menu')}10`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
        }}>
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "12px"
            }}>
                <div style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: `linear-gradient(135deg, ${customApp('menu')}, ${customApp('menu')}dd)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 4px 12px ${customApp('menu')}30`
                }}>
                    <span className="material-icons" style={{
                        fontSize: "20px",
                        color: "#fff"
                    }}>
                        {icon}
                    </span>
                </div>
                <div>
                    <h3 style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        color: "#1a1a1a",
                        margin: 0,
                        letterSpacing: "-0.3px"
                    }}>
                        {translate(title)}
                    </h3>
                    {subtitle && (
                        <p style={{
                            fontSize: "12px",
                            color: "#666",
                            margin: 0,
                            marginTop: "2px"
                        }}>
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>

            <div style={{
                display: "flex",
                gap: "8px",
                alignItems: "center"
            }}>
                {onToggleSuggestions && (
                    <ButtonOptions
                        text={showSuggestions ? "📋 Meu Dia" : "💡 Sugestões"}
                        active={showSuggestions}
                        onClick={onToggleSuggestions}
                        style={{
                            borderRadius: "8px",
                            padding: "8px 16px",
                            fontSize: "12px"
                        }}
                    />
                )}

                {onFilterChange && (
                    <div style={{
                        display: "flex",
                        gap: "4px",
                        background: "rgba(255,255,255,0.5)",
                        padding: "4px",
                        borderRadius: "8px"
                    }}>
                        <button
                            onClick={() => onFilterChange('EndDate')}
                            style={{
                                padding: "6px 12px",
                                borderRadius: "6px",
                                border: "none",
                                backgroundColor: currentFilter === 'EndDate' ? customApp('menu') : 'transparent',
                                color: currentFilter === 'EndDate' ? "#fff" : "#666",
                                fontSize: "11px",
                                fontWeight: "600",
                                cursor: "pointer",
                                transition: "all 0.2s ease"
                            }}
                        >
                            📅 Data
                        </button>
                        <button
                            onClick={() => onFilterChange('name')}
                            style={{
                                padding: "6px 12px",
                                borderRadius: "6px",
                                border: "none",
                                backgroundColor: currentFilter === 'name' ? customApp('menu') : 'transparent',
                                color: currentFilter === 'name' ? "#fff" : "#666",
                                fontSize: "11px",
                                fontWeight: "600",
                                cursor: "pointer",
                                transition: "all 0.2s ease"
                            }}
                        >
                            🔤 Nome
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
});

// Componente de Welcome Banner Moderno
const WelcomeBanner = memo(({ user, timeMessage, onHideBanner }) => {
    return (
        <div style={{
            background: `linear-gradient(135deg, 
                ${customApp('menu')}15 0%, 
                transparent 50%, 
                ${customApp('menu')}05 100%)`,
            borderRadius: "20px",
            padding: "24px",
            marginBottom: "32px",
            position: "relative",
            overflow: "hidden"
        }}>
            <div style={{
                fontSize: "18px",
                fontWeight: "700",
                color: customApp("menu"),
                marginBottom: "24px",
                textAlign: "center"
            }}>
                {translate(timeMessage, 1)}, {user?.displayName || user?.name || ""}! 👋
            </div>

            <GridContainer>
                <GridItem xs={12} md={6}>
                    <div style={{
                        background: "rgba(255,255,255,0.9)",
                        borderRadius: "16px",
                        padding: "20px",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.3)"
                    }}>
                        <img
                            alt="MACHEN_SUPPORT"
                            src={MockupSupport}
                            style={{
                                width: "100%",
                                height: "auto",
                                borderRadius: "12px",
                                marginBottom: "16px"
                            }}
                        />
                        <div style={{
                            fontSize: "14px",
                            color: "#555",
                            marginBottom: "16px",
                            lineHeight: "1.5"
                        }} dangerouslySetInnerHTML={{
                            __html: nl2br(translate("$__supportWelcomeMessage", 1))
                        }} />
                        <CustomButton
                            title={translate(`$__clickToaccessSupportPage`, '*')}
                            text={translate("$__clickToaccessSupportPage")}
                            color={customApp('menu')}
                            icon={'help_outline'}
                            onClick={() => window.open("https://support.machen.ai/", "_blank")}
                            size={'14px'}
                        />
                    </div>
                </GridItem>

                <GridItem xs={12} md={6}>
                    <div style={{
                        background: "rgba(255,255,255,0.9)",
                        borderRadius: "16px",
                        padding: "20px",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        position: "relative"
                    }}>
                        <img
                            alt="MACHEN_APP"
                            src={MockupApp}
                            style={{
                                width: "100%",
                                height: "auto",
                                borderRadius: "12px"
                            }}
                        />
                        <button
                            onClick={onHideBanner}
                            style={{
                                position: "absolute",
                                top: "12px",
                                right: "12px",
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                backgroundColor: "rgba(0,0,0,0.7)",
                                color: "#fff",
                                border: "none",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "16px",
                                transition: "all 0.2s ease"
                            }}
                            title={translate("$__hideSupportAndAppBanner")}
                        >
                            ✕
                        </button>
                    </div>
                </GridItem>
            </GridContainer>
        </div>
    );
});

// Componente de Planos Favoritos
const FavoritePlans = memo(({ plans, onPlanClick }) => {
    if (plans.length === 0) return null;

    return (
        <div style={{
            background: `linear-gradient(135deg, 
                rgba(255,255,255,0.95) 0%, 
                rgba(255,255,255,0.8) 100%)`,
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            padding: "24px",
            marginBottom: "32px",
            border: `1px solid ${customApp('menu')}20`,
            boxShadow: `0 4px 16px ${customApp('menu')}10`
        }}>
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px"
            }}>
                <h3 style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: customApp("menu"),
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                }}>
                    <span className="material-icons">favorite</span>
                    {translate("$__favoritePlans")}
                </h3>

                {plans.length > 3 && (
                    <CustomButton
                        title={`${translate(`$__viewAllFavorites`, '*')} (${plans.length})`}
                        text={`Ver Todos (${plans.length})`}
                        color={customApp('menu')}
                        icon={'favorite'}
                        transparent
                        onClick={() => history.push(`sp#tab/home`)}
                        size={'12px'}
                    />
                )}
            </div>

            <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "16px"
            }}>
                {plans.slice(0, 6).map((plan, index) => (
                    <div
                        key={plan._id}
                        onClick={() => onPlanClick(plan)}
                        style={{
                            background: "rgba(255,255,255,0.8)",
                            borderRadius: "12px",
                            padding: "16px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            border: `1px solid ${customApp('menu')}20`,
                            animationDelay: `${index * 0.1}s`,
                            animation: "fadeInUp 0.5s ease-out forwards"
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-4px)";
                            e.currentTarget.style.boxShadow = `0 8px 24px ${customApp('menu')}20`;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0px)";
                            e.currentTarget.style.boxShadow = "none";
                        }}
                    >
                        <Card
                            data={plan}
                            expanded
                            Avatar
                            ignoreList
                        />
                    </div>
                ))}
            </div>
        </div>
    );
});

function ResumeTasks(props) {
    const { db, session } = reduxStore.getState();
    const { users } = db;

    const [viewSuggestions, setViewSuggestions] = useState(false);
    const [nextTasksOrderBy, setNextTasksOrderBy] = useState("EndDate");
    const [disabledTypes, setDisabledTypes] = useState([
        "guideline", "guidelineD", "guidelineR", "guidelineC", "guidelineE",
        "objective", "goal", "link", "insight"
    ]);
    const [cookies, setCookie] = useCookies(['banner']);
    const [myCards, setMyCards] = useState([]);

    // Effects e lógica existente mantidos...
    React.useEffect(() => {
        let s = Object.keys(db.cards || {})
            .filter(a =>
                db.cards[a]
                && !db.cards[a].checklist
                && !db.cards[a].deleted
                && (
                    !props.hidePrivate
                    || (props.hidePrivate && !db.cards[a].private)
                )
            ).map(c => db.cards[c]);
        if (JSON.stringify(s) !== JSON.stringify(myCards))
            setMyCards(s);
    }, [props.cards, db.cards, myCards, props.hidePrivate]);

    // Callbacks
    const hideSupportAndAppBanner = useCallback(async () => {
        const { socket } = reduxStore.getState().functions;
        setCookie('banner', true, { path: '/' });
        try {
            socket.emit("data", {
                module: "user",
                method: "post",
                action: "preffers"
            }, {
                hideSupportAndAppBanner: true
            }, data => {
                if (data.error) console.log("hideSupportAndAppBanner", data.error);
            });
        } catch (e) {
            console.log(e);
        }
    }, [setCookie]);

    const handleAddToMyDay = useCallback((task) => {
        setMyDay(props, {
            _id: task._id,
            userId: session._id,
            option: true
        });
    }, [props, session._id]);

    const handleTaskClick = useCallback((task) => {
        console.log('Task clicked:', task);
        // Implementar navegação para detalhes da task
    }, []);

    const handlePlanClick = useCallback((plan) => {
        history.push(`sp#${plan._id}/dashboard`);
    }, []);

    // Dados processados
    const today = moment().format("YYYY-MM-DD");
    const user = users && Object.keys(users).length > 0 ?
        users[Object.keys(users).find(a => session?._id && users[a]?._id?.indexOf(session._id) > -1)] : {};

    const timeMessage = useMemo(() => {
        const now = new Date();
        const hour = now.getHours();
        if (hour >= 0 && hour < 5) return "$__goodNight";
        if (hour >= 5 && hour < 12) return "$__goodMorning";
        if (hour >= 12 && hour < 18) return "$__goodAfternoon";
        return "$__goodNight";
    }, []);

    // Sugestões de tarefas
    const myDayTasksSuggestions = useMemo(() => {
        return myCards
            .filter(a => disabledTypes.length === 0 || !disabledTypes.includes(a.type))
            .filter(a => {
                if (
                    a && a._users && a._users[session._id] &&
                    a.status !== "completed" &&
                    a.type !== "chatGroup" &&
                    a.type !== "plan"
                ) {
                    const userDay = a._users[session._id].myDay;
                    const isResponsible = a._users[session._id].isResponsible;

                    return (
                        (userDay && moment(userDay * 1000).format("YYYY-MM-DD") !== today) ||
                        (isResponsible &&
                            ((a.endDate && moment(a.endDate * 1000).unix('x') < moment().unix('x')) ||
                                (a.startDate && moment(a.startDate * 1000).unix('x') > moment(today).subtract(30, "days").unix('x'))) &&
                            (!userDay || moment(userDay * 1000).format("YYYY-MM-DD") !== today))
                    );
                }
                return false;
            })
            .sort((a, b) => {
                let compareDateA = a._users[session._id].myDay || a.endDate || a.startDate || 0;
                let compareDateB = b._users[session._id].myDay || b.endDate || b.startDate || 0;
                return compareDateB - compareDateA;
            });
    }, [myCards, disabledTypes, session._id, today]);

    // Próximas tarefas
    const nextTasks = useMemo(() => {
        return myCards.filter(a =>
            a.status !== "completed" &&
            a.name &&
            !a.deleted &&
            !a.archived &&
            a._users &&
            a._users[session._id] &&
            (!a._users[session._id].myDay ||
                moment(a._users[session._id].myDay * 1000).format("YYYY-MM-DD") !== today) &&
            ((a.endDate &&
                moment(a.endDate * 1000).format("YYYY-MM-DD") >= today &&
                moment(a.endDate * 1000).format("YYYY-MM-DD") <= moment(today).add(10, "days").format("YYYY-MM-DD")) ||
                (a.startDate &&
                    moment(a.startDate * 1000).format("YYYY-MM-DD") >= today &&
                    moment(a.startDate * 1000).format("YYYY-MM-DD") <= moment(today).add(10, "days").format("YYYY-MM-DD")))
        ).sort((a, b) => {
            let paramA = a.name.toLowerCase().trim();
            let paramB = b.name.toLowerCase().trim();

            if (nextTasksOrderBy === 'IniDate') {
                paramA = parseInt(a.startDate || 0);
                paramB = parseInt(b.startDate || 0);
            }
            if (nextTasksOrderBy === 'EndDate') {
                paramA = parseInt(a.endDate || 0);
                paramB = parseInt(b.endDate || 0);
            }

            return paramA < paramB ? -1 : paramA > paramB ? 1 : 0;
        });
    }, [myCards, session._id, today, nextTasksOrderBy]);

    // Planos favoritos
    const favoritePlans = useMemo(() => {
        return Object.keys(db.cards || {})
            .filter(fil =>
                db.cards[fil] &&
                !db.cards[fil].deleted &&
                !db.cards[fil].archived &&
                db.cards[fil].type === "plan" &&
                db.cards[fil]._users &&
                db.cards[fil]._users[session._id] &&
                db.cards[fil].status !== "completed" &&
                db.cards[fil]._isFavorite &&
                (!props.hidePrivate || !db.cards[fil].private)
            )
            .map(id => db.cards[id])
            .sort((a, b) => {
                let updA = a.updated_at?.low || 0;
                let updB = b.updated_at?.low || 0;
                return updB - updA;
            });
    }, [db.cards, session._id, props.hidePrivate]);

    return (
        <ErrorBoundary>
            <div style={{
                width: "100%",
                background: `linear-gradient(135deg, 
                    ${customApp('menu')}03 0%, 
                    transparent 50%, 
                    ${customApp('menu')}02 100%)`,
                borderRadius: "24px",
                padding: "20px",
                position: "relative"
            }}>
                {/* Background Pattern */}
                <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `radial-gradient(circle at 10% 10%, ${customApp('menu')}05 0%, transparent 50%),
                                    radial-gradient(circle at 90% 90%, ${customApp('menu')}03 0%, transparent 50%)`,
                    pointerEvents: "none"
                }} />

                {/* Welcome Banner */}
                {!cookies.banner && !session.hideSupportAndAppBanner && (
                    <WelcomeBanner
                        user={user}
                        timeMessage={timeMessage}
                        onHideBanner={hideSupportAndAppBanner}
                    />
                )}

                {/* Favorite Plans */}
                <FavoritePlans
                    plans={favoritePlans}
                    onPlanClick={handlePlanClick}
                />

                {/* Tasks Sections */}
                <GridContainer style={{ position: "relative", zIndex: 1 }}>
                    {/* My Day / Suggestions */}
                    <GridItem xs={12} md={6} style={{ paddingRight: "12px" }}>
                        <SectionHeader
                            title={viewSuggestions ? "$__taskSuggestions" : "$__myDay"}
                            subtitle={viewSuggestions ?
                                `${myDayTasksSuggestions.length} sugestões disponíveis` :
                                "Suas tarefas para hoje"
                            }
                            icon={viewSuggestions ? "lightbulb" : "today"}
                            showSuggestions={viewSuggestions}
                            onToggleSuggestions={() => setViewSuggestions(!viewSuggestions)}
                        />

                        <div style={{
                            maxHeight: "500px",
                            overflowY: "auto",
                            paddingRight: "8px",
                            scrollbarWidth: "thin",
                            scrollbarColor: `${customApp('menu')}40 transparent`
                        }}>
                            {viewSuggestions ? (
                                <>
                                    <div style={{ marginBottom: "16px" }}>
                                        <SelectCardTypes
                                            options={disabledTypes}
                                            onChange={setDisabledTypes}
                                        />
                                    </div>
                                    {myDayTasksSuggestions.length > 0 ? (
                                        myDayTasksSuggestions.map((task, index) => (
                                            <ModernTaskCard
                                                key={task._id}
                                                task={task}
                                                index={index}
                                                onTaskClick={handleTaskClick}
                                                onAddToMyDay={handleAddToMyDay}
                                            />
                                        ))
                                    ) : (
                                        <div style={{
                                            textAlign: "center",
                                            padding: "40px",
                                            color: "#666",
                                            background: "rgba(255,255,255,0.5)",
                                            borderRadius: "12px",
                                            backdropFilter: "blur(10px)"
                                        }}>
                                            <span className="material-icons" style={{
                                                fontSize: "48px",
                                                marginBottom: "12px",
                                                display: "block",
                                                opacity: 0.3
                                            }}>
                                                lightbulb
                                            </span>
                                            Nenhuma sugestão disponível
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Checklist
                                    noChecklist
                                    noDelete
                                    showCard
                                    myDay
                                    db={"cards"}
                                />
                            )}
                        </div>
                    </GridItem>

                    {/* Next Tasks */}
                    <GridItem xs={12} md={6} style={{ paddingLeft: "12px" }}>
                        <SectionHeader
                            title="$__nextTasks"
                            subtitle={`${nextTasks.length} tarefas nos próximos 10 dias`}
                            icon="schedule"
                            currentFilter={nextTasksOrderBy}
                            onFilterChange={setNextTasksOrderBy}
                        />

                        <div style={{
                            maxHeight: "500px",
                            overflowY: "auto",
                            paddingRight: "8px",
                            scrollbarWidth: "thin",
                            scrollbarColor: `${customApp('menu')}40 transparent`
                        }}>
                            {nextTasks.length > 0 ? (
                                nextTasks.map((task, index) => (
                                    <ModernTaskCard
                                        key={task._id}
                                        task={task}
                                        index={index}
                                        onTaskClick={handleTaskClick}
                                    />
                                ))
                            ) : (
                                <div style={{
                                    textAlign: "center",
                                    padding: "40px",
                                    color: "#666",
                                    background: "rgba(255,255,255,0.5)",
                                    borderRadius: "12px",
                                    backdropFilter: "blur(10px)"
                                }}>
                                    <span className="material-icons" style={{
                                        fontSize: "48px",
                                        marginBottom: "12px",
                                        display: "block",
                                        opacity: 0.3
                                    }}>
                                        schedule
                                    </span>
                                    Nenhuma tarefa próxima
                                </div>
                            )}
                        </div>
                    </GridItem>
                </GridContainer>

                <style jsx>{`
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                `}</style>
            </div>
        </ErrorBoundary>
    );
}

const mapStateToProps = (store) => ({
    cards: store.db.cards,
    hidePrivate: store.controls.hidePrivate,
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(reduxActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ResumeTasks)